import { FC, useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useProfile } from '@/hooks/useProfile';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { AvatarUpload } from './AvatarUpload';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const profileSchema = z.object({
  studentName: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  tutorName: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  whatsapp: z.string().min(8, "Ingresa un número de WhatsApp válido"),
  country_code: z.string(),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

interface ProfileFormProps {
  userProfile: any | null;
  onProfileUpdate?: () => void;
}

const phoneInputStyles = {
  container: {
    width: '100%',
  },
  input: {
    width: '100%',
    height: '40px',
    borderRadius: '0.375rem',
    paddingLeft: '48px',
    border: '1px solid #e2e8f0',
  },
  button: {
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem 0 0 0.375rem',
    background: '#f8fafc',
  },
  dropdown: {
    width: '300px',
    maxHeight: '200px',
    overflowY: 'auto',
    borderRadius: '0.375rem',
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },
};

export const ProfileForm: FC<ProfileFormProps> = ({ userProfile, onProfileUpdate }) => {
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      studentName: '',
      tutorName: '',
      whatsapp: '',
      country_code: 'uy',
    },
  });

  useEffect(() => {
    if (userProfile) {
      // Formatear el número de WhatsApp
      let whatsappNumber = userProfile.whatsapp || '';
      if (whatsappNumber && !whatsappNumber.startsWith('+')) {
        whatsappNumber = '+' + whatsappNumber;
      }

      // Actualizar el formulario con los datos del perfil
      form.reset({
        studentName: userProfile.student_name || '',
        tutorName: userProfile.tutor_name || '',
        whatsapp: whatsappNumber,
        country_code: (userProfile.country_code || 'uy').toLowerCase(),
      });
    }
  }, [userProfile]);

  const updateAvatar = async (avatarUrl: string) => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user?.id) {
        throw new Error('No se encontró el ID del usuario');
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          avatar_url: avatarUrl,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.user.id);

      if (error) throw error;

      // Notificar al padre que el perfil se actualizó
      if (onProfileUpdate) {
        onProfileUpdate();
      }
    } catch (error) {
      console.error('Error updating avatar:', error);
      throw error;
    }
  };

  const onSubmit = async (data: ProfileFormValues) => {
    try {
      setIsSaving(true);
      
      const { data: user } = await supabase.auth.getUser();
      if (!user.user?.id) {
        throw new Error('No se encontró el ID del usuario');
      }

      // Formatear el número de WhatsApp
      let formattedWhatsapp = data.whatsapp;
      
      // Si el número empieza con '+', remover el '+'
      if (formattedWhatsapp.startsWith('+')) {
        formattedWhatsapp = formattedWhatsapp.substring(1);
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          student_name: data.studentName,
          tutor_name: data.tutorName,
          whatsapp: formattedWhatsapp,
          country_code: data.country_code.toUpperCase(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.user.id);

      if (error) throw error;

      toast({
        title: "¡Perfil actualizado!",
        description: "Los cambios han sido guardados correctamente.",
      });

      // Notificar al padre que el perfil se actualizó
      if (onProfileUpdate) {
        onProfileUpdate();
      }

    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudieron guardar los cambios. Por favor, intenta de nuevo.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 bg-white p-6 rounded-lg shadow-sm">
        <h2 className="text-xl font-semibold mb-4">Información Personal</h2>

        <div className="flex justify-center mb-6">
          <AvatarUpload
            url={userProfile?.avatar_url}
            onUpload={updateAvatar}
            name={userProfile?.student_name || 'U'}
          />
        </div>
        
        <FormField
          control={form.control}
          name="studentName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nombre del Estudiante</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Nombre completo del estudiante" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="tutorName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nombre del Tutor</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Nombre completo del tutor" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="whatsapp"
          render={({ field }) => (
            <FormItem>
              <FormLabel>WhatsApp</FormLabel>
              <FormControl>
                <PhoneInput
                  country="uy"
                  value={field.value}
                  onChange={(phone) => field.onChange(phone)}
                  containerStyle={phoneInputStyles.container}
                  inputStyle={phoneInputStyles.input}
                  buttonStyle={phoneInputStyles.button}
                  dropdownStyle={phoneInputStyles.dropdown}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Guardar Cambios
            </>
          )}
        </Button>
      </form>
    </Form>
  );
};